import { DateTime } from "luxon";
import z from "zod";

import { overrideZodInfer } from "./common";

export const DateFilterKeys = z.enum([
  "ALL_TIME",
  "LAST_MONTH",
  "THIS_MONTH",
  "LAST_YEAR",
  "THIS_YEAR",
  "CUSTOM_DATE_RANGE",
]);

export type DateFilterKey = z.infer<typeof DateFilterKeys>;

const DateTimeSchema = z.preprocess(
  value => {
    if (typeof value === "string" || value instanceof Date) {
      return DateTime.fromJSDate(new Date(value));
    }
    return value as DateTime;
  },
  z.custom<DateTime>(value => value instanceof DateTime)
) as z.ZodEffects<
  z.ZodType<DateTime<boolean>, z.ZodTypeDef, DateTime<boolean>>,
  DateTime<boolean>,
  DateTime<boolean>
>;

export const DateFilterFormSchema = z.object({
  dateFilter: z
    .object({
      key: DateFilterKeys,
      startDate: z.string().or(DateTimeSchema).nullish(),
      endDate: z.string().or(DateTimeSchema).nullish(),
    })
    .nullish(),
});

export type TargetingDateFilter = NonNullable<
  z.infer<typeof DateFilterFormSchema>["dateFilter"]
>;

export enum AudienceTargetingFilterCategory {
  Location = "location",
  DonationHistory = "donationHistory",
  Engagement = "engagement",
  CustomField = "customField",
  SavedSearch = "savedSearch",
}

const AudienceTargetingFilterCategorySchema = z.nativeEnum(
  AudienceTargetingFilterCategory
);

export const AudienceTargetingFilterOperationSchema = z.enum([
  "isInAreaCode",
  "notInAreaCode",
  "isInState",
  "isInCustomState",
  "notInState",
  "notInCustomState",
  "stateIsNull",
  "isInZip",
  "isInCustomZip",
  "notInZip",
  "notInCustomZip",
  "isInZipRadius",
  "isInCustomZipRadius",
  "notInZipRadius",
  "notInCustomZipRadius",
  "zipIsNull",
  "contains",
  "notContains",
  "matches",
  "equals",
  "notEquals",
  "boolean",
  "customFalse",
  "customTrue",
  "customIsNull",
  "between",
  "notBetween",
  "oneOf",
  "notOneOf",
  "gt",
  "lt",
  "isNull",
  "isInSavedSearch",
  "totalDonationGt",
  "totalDonationLt",
  "totalDonationBetween",
  "isRecurringDonor",
  "notRecurringDonor",
  "recurringDonationAmountGt",
  "recurringDonationAmountLt",
  "recurringDonationAmountBetween",
  "hasDonated",
  "notHasDonated",
  "lastDonationGt",
  "lastDonationLt",
  "lastDonationBetween",
  "numberOfDonationsGt",
  "numberOfDonationsLt",
  "numberOfDonationsBetween",
  "averageDonationGt",
  "averageDonationLt",
  "averageDonationBetween",
  "highestDonationGt",
  "highestDonationLt",
  "highestDonationBetween",
  "hasMobileDonation",
  "notHasMobileDonation",
  "hasDonatedToCampaigns",
  "notHasDonatedToCampaigns",
  "hasReplied",
  "notHasReplied",
  "hasRepliedToCampaigns",
  "notHasRepliedToCampaigns",
]);

export type AudienceTargetingFilterOperation = z.infer<
  typeof AudienceTargetingFilterOperationSchema
>;

const VALUE_NOT_REQUIRED_FILTER_OPERATIONS: Set<AudienceTargetingFilterOperation> =
  new Set([
    "stateIsNull",
    "zipIsNull",
    "customFalse",
    "customTrue",
    "customIsNull",
    "isNull",
    "isRecurringDonor",
    "notRecurringDonor",
    "hasDonated",
    "notHasDonated",
    "hasMobileDonation",
    "notHasMobileDonation",
  ]);

export const AudienceTargetingFieldTypeSchema = z.enum([
  "STATE",
  "ZIP",
  "ZIP_RADIUS",
  "AREA_CODE",
  "NUMBER",
  "NUMBER_RANGE",
  "BOOLEAN_TRUE",
  "BOOLEAN_FALSE",
  "TEXT",
  "ENUM",
  "BOOLEAN",
  "CURRENCY",
  "CURRENCY_RANGE",
  "IS_NULL",
  "SAVED_SEARCH",
  "CAMPAIGN",
]);

export type AudienceTargetingFieldType = z.infer<
  typeof AudienceTargetingFieldTypeSchema
>;

export const TargetGroupSchemaBase = z.object({
  ...DateFilterFormSchema.shape,
});

// Explicit interfaces to break the circular type inference.
// TypeScript supports mutually recursive interfaces; Zod v3 z.infer does not.
// See: https://github.com/colinhacks/zod/issues/4881
export interface TargetFilterSearchBody {
  targetGroups?: TargetGroup[];
}

export interface AudienceFilterConfig {
  centerZips?: string[] | null;
  radius?: string | null;
  searchBody?: TargetFilterSearchBody | null;
}

// "Base" filter type: filterOperation and fieldType may be null (incomplete form state)
type TargetFilterBase = {
  filterCategory: AudienceTargetingFilterCategory;
  filterOperation?: AudienceTargetingFilterOperation | null;
  fieldType?: AudienceTargetingFieldType | null;
  values?: (string | number)[] | null;
  value?: string | number | boolean | null;
  fieldName?: string | null;
  filterConfig?: AudienceFilterConfig | null;
};

// Full filter type for database-stored filters: filterOperation and fieldType are required
export interface TargetFilter extends Omit<
  TargetFilterBase,
  "filterOperation" | "fieldType"
> {
  filterOperation: AudienceTargetingFilterOperation;
  fieldType: AudienceTargetingFieldType;
}

export interface TargetGroup extends z.infer<typeof TargetGroupSchemaBase> {
  clauses: TargetFilter[];
}

const TargetFilterSearchBodySchema: z.ZodType<TargetFilterSearchBody> =
  z.object({
    // This has to be built lazily, and we have to call it an "any" type
    // because zod v3 doesn't do well with circular types.
    // We should be able to clean this up after update to v4
    // https://github.com/colinhacks/zod/issues/4881
    targetGroups: z
      .array(z.lazy((): z.ZodType<TargetGroup> => TargetGroupSchema))
      .optional(),
  });

const FilterConfigSchema: z.ZodType<AudienceFilterConfig> = z.object({
  centerZips: z.array(z.string()).nullish(),
  radius: z.string().nullish(),
  searchBody: TargetFilterSearchBodySchema.nullish(),
});

function checkRangeValue(
  data: {
    fieldType?: AudienceTargetingFieldType | null;
    values?: unknown[] | null;
  },
  index: number
) {
  const isRangeType =
    data.fieldType === "NUMBER_RANGE" || data.fieldType === "CURRENCY_RANGE";
  if (isRangeType) {
    if (data.values === null) {
      return false;
    }
    return (
      data.values?.[index] !== undefined &&
      data.values?.[index] !== "" &&
      !Number.isNaN(Number(data.values?.[index]))
    );
  }
  return true;
}

function rangeValueError(index: number) {
  return {
    message: "Must be a number",
    path: ["values", index],
  };
}

// This "base" schema includes nullish filterOperation, fieldType, which
// are then required in a "refine". That means that typescript will allow
// this fields to be null/undefined, but zod validation will fail. We need
// this to represent incomplete form states in a typesafe way.
export const TargetFilterSchemaBase: z.ZodType<TargetFilterBase> = z
  .object({
    filterCategory: AudienceTargetingFilterCategorySchema,
    filterOperation: AudienceTargetingFilterOperationSchema.nullish(),
    fieldType: AudienceTargetingFieldTypeSchema.nullish(),
    values: z.array(z.string().or(z.number())).nullish(),
    value: z.union([z.string(), z.number(), z.boolean()]).nullish(),
    fieldName: z.string().nullish(),
    filterConfig: FilterConfigSchema.nullish(),
  })
  .refine(data => !!data.filterOperation, {
    message: "Filter operation is required",
    path: ["filterOperation"],
  })
  .refine(data => !!data.fieldType, {
    message: "Field type is required",
    path: ["fieldType"],
  })
  // TODO: these refines can probably be replaced with a discriminated union
  .refine(
    data => {
      if (data.fieldType === "NUMBER" || data.fieldType === "CURRENCY") {
        if (data.value === null || data.value === undefined) {
          return true;
        }
        return !Number.isNaN(Number(data.value));
      }
      return true;
    },
    () => ({
      message: "Value must be a number",
      path: ["value"],
    })
  )
  .refine(
    data => checkRangeValue(data, 0),
    () => rangeValueError(0)
  )
  .refine(
    data => checkRangeValue(data, 1),
    () => rangeValueError(1)
  )
  .refine(
    data => {
      if (
        data.fieldType === "NUMBER_RANGE" ||
        data.fieldType === "CURRENCY_RANGE"
      ) {
        if (
          data.values === null ||
          data.values === undefined ||
          data.values?.some(v => v === "" || v === null || v === undefined)
        ) {
          return true;
        }

        const result = Number(data.values[0]) < Number(data.values[1]);
        return result;
      }
      return true;
    },
    () => ({
      message: "The lower limit must be less than the upper limit",
      path: ["values"],
    })
  )
  .refine(
    data => {
      if (VALUE_NOT_REQUIRED_FILTER_OPERATIONS.has(data.filterOperation!)) {
        return true;
      }
      const hasValue = data.value !== undefined && data.value !== "";
      const hasValues =
        data.values !== undefined &&
        data.values?.length &&
        data.values.length > 0;
      return hasValue || hasValues;
    },
    data => {
      switch (data.fieldType!) {
        case "STATE": {
          return {
            message: "At least one state must be selected",
            path: ["values"],
          };
        }
        case "ZIP":
        case "ZIP_RADIUS": {
          return {
            message: "At least one zip code must be provided",
            path: ["values"],
          };
        }
        case "NUMBER": {
          return {
            message: "A number is required",
            path: ["value"],
          };
        }
        case "BOOLEAN": {
          return {
            message: "A value is required",
            path: ["value"],
          };
        }
        case "CAMPAIGN":
        case "ENUM":
        case "AREA_CODE": {
          return {
            message: "At least one option must be selected",
            path: ["values"],
          };
        }
        case "NUMBER_RANGE":
        case "CURRENCY_RANGE": {
          return {
            message: "A number range is required",
            path: ["values"],
          };
        }
        case "CURRENCY":
        case "BOOLEAN_FALSE":
        case "BOOLEAN_TRUE":
        case "IS_NULL":
        case "SAVED_SEARCH":
        case "TEXT": {
          return {
            message: "A value is required",
            path: ["value"],
          };
        }
      }
    }
  );

// This extension of the "base" schema is for fully-built filter objects.
// The type requires filterOperation and fieldType to be defined.
// This is what we use to parse filters stored in the database.
const TargetFilterSchema: z.ZodType<TargetFilter> = overrideZodInfer(
  TargetFilterSchemaBase
)<{
  filterOperation: AudienceTargetingFilterOperation;
  fieldType: AudienceTargetingFieldType;
}>();

const TargetGroupSchema = TargetGroupSchemaBase.extend({
  clauses: z.array(TargetFilterSchema),
});

export const AudienceTargetingSchema = z.object({
  targetGroups: z.array(TargetGroupSchema),
});
export type AudienceTargeting = z.infer<typeof AudienceTargetingSchema>;

export const TARGETING_FILTER_ANY_VALUES = ["__ANY__"] as const;
